import { useMemo } from 'react';
import { Trash2 } from 'lucide-react';

import { Field } from '@/components/Field';
import { NumberInput } from '@/components/NumberInput';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { headcountAtMonth } from '@/domain/headcount';
import { formatEur, formatQuantity } from '@/lib/format';
import { useSimulatorState } from '@/store/useSimulatorState';

export function TeamCostsPanel() {
  const {
    team,
    initialDeficit,
    setInitialDeficit,
    acquisition,
    categories,
    fixedCosts,
    recurringCosts,
    updateTeam,
    addCategory,
    updateCategory,
    removeCategory,
    addFixedCost,
    updateFixedCost,
    removeFixedCost,
    addRecurringCost,
    updateRecurringCost,
    removeRecurringCost,
  } = useSimulatorState();

  const recurringSum = recurringCosts.reduce((sum, c) => sum + c.monthlyAmount, 0);
  const chargesMul = team.chargesMultiplier ?? 1;
  const teamMonthlyCost = (headcount: number) =>
    (headcount * team.avgAnnualCostPerFTE * chargesMul) / 12 + recurringSum;
  const monthlyCost = teamMonthlyCost(team.headcount);
  const totalFixed = fixedCosts.reduce((sum, c) => sum + c.amount, 0);

  const rampEnabled = team.headcountRamp?.enabled === true;
  const rampModel = acquisition.model ?? 'constant';
  const rampMonths = acquisition.rampUpMonths ?? 0;

  // Texte dérivé : effectif au mois 1 et à la fin de la montée (si active).
  // Si la montée est désactivée, on affiche l'effectif constant.
  // Si la montée n'a pas de durée paramétrée (constant / rampUpMonths=0), on saute au plateau.
  const rampSummary = useMemo(() => {
    if (!rampEnabled) {
      return `Effectif constant : ${formatQuantity(team.headcount)} ETP`;
    }
    const target = team.headcountRamp?.targetHeadcount ?? team.headcount;
    if (rampModel === 'constant' || rampMonths <= 0) {
      return `Effectif de croisière : ${formatQuantity(target)} ETP (constant, pas de montée en charge)`;
    }
    const atMonth1 = headcountAtMonth(1, { team, acquisition });
    const atRampEnd = headcountAtMonth(rampMonths, { team, acquisition });
    return `Effectif : mois 1 → ${formatQuantity(atMonth1)} ETP · mois ${rampMonths} → ${formatQuantity(atRampEnd)} ETP`;
  }, [rampEnabled, rampModel, rampMonths, team, acquisition]);

  return (
    <div className="flex flex-col gap-6 py-2">
      {/* Situation de départ */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Situation de départ</h3>
        <Field
          id="initialDeficit"
          label="Déficit initial (€)"
          tooltip="Trésorerie déjà consommée avant le mois 1 (pertes accumulées, investissements). Le cash flow démarre à -ce montant et doit être remboursé avant le break-even global (ROI)."
        >
          <NumberInput
            id="initialDeficit"
            value={initialDeficit ?? 0}
            onChange={(v) => setInitialDeficit(v)}
            min={0}
            decimals={0}
          />
        </Field>
      </section>

      <Separator />

      {/* Équipe */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Équipe</h3>
        <Field
          id="headcount"
          label="Nombre d'ETP"
          tooltip={
            rampEnabled
              ? "Effectif de départ. L'effectif cible (plateau) se règle ci-dessous."
              : 'Fractions autorisées (ex : 0,5 pour un mi-temps).'
          }
        >
          <NumberInput
            id="headcount"
            value={team.headcount}
            onChange={(v) => updateTeam({ headcount: v })}
            min={0}
            decimals={2}
          />
        </Field>
        <Field id="avgAnnualCostPerFTE" label="Coût moyen / ETP / an">
          <NumberInput
            id="avgAnnualCostPerFTE"
            value={team.avgAnnualCostPerFTE}
            onChange={(v) => updateTeam({ avgAnnualCostPerFTE: v })}
            min={0}
            decimals={0}
          />
        </Field>
        <Field
          id="chargesMultiplier"
          label="Multiplicateur de charges"
          tooltip="Multiplicateur appliqué au salaire brut pour obtenir le coût employeur (ex : 1,42 = charges patronales à 42 %)."
        >
          <NumberInput
            id="chargesMultiplier"
            value={team.chargesMultiplier ?? 1.42}
            onChange={(v) => updateTeam({ chargesMultiplier: v })}
            min={0}
            decimals={2}
          />
        </Field>

        {/* Montée en charge de l'effectif — optionnelle, suit la courbe acquisition */}
        <div className="mt-2 flex flex-col gap-2 rounded-md border border-dashed p-3">
          <label
            htmlFor="headcountRampEnabled"
            className="flex cursor-pointer items-center gap-2 text-sm"
          >
            <Checkbox
              id="headcountRampEnabled"
              checked={rampEnabled}
              onCheckedChange={(checked) => {
                if (checked) {
                  updateTeam({
                    headcountRamp: {
                      enabled: true,
                      // Par défaut, la cible vaut l'effectif actuel pour ne pas surprendre l'utilisateur.
                      targetHeadcount: team.headcountRamp?.targetHeadcount ?? team.headcount,
                    },
                  });
                } else {
                  updateTeam({ headcountRamp: undefined });
                }
              }}
            />
            <span>Activer la montée en charge de l&apos;effectif</span>
          </label>
          <p className="text-xs text-muted-foreground">
            Suit la même courbe que l&apos;acquisition (constante / linéaire / courbe en S, durée
            paramétrée dans le panneau « Acquisition »).
          </p>

          {rampEnabled && (
            <>
              <Field
                id="targetHeadcount"
                label="Effectif cible (plateau)"
                tooltip="Effectif atteint à la fin de la montée. Peut être inférieur au nombre d'ETP initial pour modéliser une réduction d'effectifs."
              >
                <NumberInput
                  id="targetHeadcount"
                  value={team.headcountRamp?.targetHeadcount ?? team.headcount}
                  onChange={(v) =>
                    updateTeam({
                      headcountRamp: {
                        enabled: true,
                        targetHeadcount: v,
                      },
                    })
                  }
                  min={0}
                  decimals={2}
                />
              </Field>
              <p className="text-xs text-muted-foreground">{rampSummary}</p>
            </>
          )}
        </div>
      </section>

      <Separator />

      {/* Catégories */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Catégories</h3>
        {categories.length === 0 && (
          <p className="text-xs text-muted-foreground">Aucune catégorie définie.</p>
        )}
        {categories.map((cat) => (
          <div key={cat.id} className="flex items-center gap-2">
            <input
              type="color"
              value={cat.color ?? '#888888'}
              aria-label={`Couleur de la catégorie ${cat.name}`}
              className="size-7 shrink-0 cursor-pointer rounded border border-input"
              onChange={(e) => updateCategory(cat.id, { color: e.target.value })}
            />
            <Input
              defaultValue={cat.name}
              className="flex-1"
              onBlur={(e) => updateCategory(cat.id, { name: e.target.value })}
              aria-label="Nom de la catégorie"
            />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`Supprimer la catégorie ${cat.name}`}
              onClick={() => removeCategory(cat.id)}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => addCategory({ name: 'Nouvelle catégorie', color: '#888888' })}
        >
          + Ajouter une catégorie
        </Button>
      </section>

      <Separator />

      {/* Coûts fixes */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Coûts fixes</h3>
        {fixedCosts.length === 0 && (
          <p className="text-xs text-muted-foreground">Aucun coût fixe défini.</p>
        )}
        {fixedCosts.map((cost) => (
          <div key={cost.id} className="flex flex-col gap-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <Input
                defaultValue={cost.label}
                className="flex-1"
                placeholder="Libellé"
                onBlur={(e) => updateFixedCost(cost.id, { label: e.target.value })}
                aria-label="Libellé du coût fixe"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Supprimer ${cost.label}`}
                onClick={() => removeFixedCost(cost.id)}
              >
                <Trash2 />
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-2 gap-2">
                <Field id={`fixed-amount-${cost.id}`} label="Montant (€)">
                  <NumberInput
                    id={`fixed-amount-${cost.id}`}
                    value={cost.amount}
                    onChange={(v) => updateFixedCost(cost.id, { amount: v })}
                    min={0}
                    decimals={0}
                  />
                </Field>
                <Field id={`fixed-month-${cost.id}`} label="Mois d'occurrence">
                  <NumberInput
                    id={`fixed-month-${cost.id}`}
                    value={cost.occursAtMonth}
                    onChange={(v) => updateFixedCost(cost.id, { occursAtMonth: Math.round(v) })}
                    min={1}
                    decimals={0}
                  />
                </Field>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium leading-none">Catégorie</label>
                <Select
                  value={cost.categoryId}
                  onValueChange={(v) => updateFixedCost(cost.id, { categoryId: v })}
                >
                  <SelectTrigger aria-label="Catégorie">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.length === 0 && (
                      <SelectItem value="" disabled>
                        Aucune catégorie
                      </SelectItem>
                    )}
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() =>
            addFixedCost({
              label: 'Nouveau coût fixe',
              amount: 0,
              categoryId: categories[0]?.id ?? '',
              occursAtMonth: 1,
            })
          }
        >
          + Ajouter un coût fixe
        </Button>
      </section>

      <Separator />

      {/* Coûts récurrents */}
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold">Coûts récurrents</h3>
        {recurringCosts.length === 0 && (
          <p className="text-xs text-muted-foreground">Aucun coût récurrent défini.</p>
        )}
        {recurringCosts.map((cost) => (
          <div key={cost.id} className="flex flex-col gap-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <Input
                defaultValue={cost.label}
                className="flex-1"
                placeholder="Libellé"
                onBlur={(e) => updateRecurringCost(cost.id, { label: e.target.value })}
                aria-label="Libellé du coût récurrent"
              />
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Supprimer ${cost.label}`}
                onClick={() => removeRecurringCost(cost.id)}
              >
                <Trash2 />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field id={`rec-amount-${cost.id}`} label="Montant / mois (€)">
                <NumberInput
                  id={`rec-amount-${cost.id}`}
                  value={cost.monthlyAmount}
                  onChange={(v) => updateRecurringCost(cost.id, { monthlyAmount: v })}
                  min={0}
                  decimals={0}
                />
              </Field>
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium leading-none">Catégorie</label>
                <Select
                  value={cost.categoryId}
                  onValueChange={(v) => updateRecurringCost(cost.id, { categoryId: v })}
                >
                  <SelectTrigger aria-label="Catégorie">
                    <SelectValue placeholder="Catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.length === 0 && (
                      <SelectItem value="" disabled>
                        Aucune catégorie
                      </SelectItem>
                    )}
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() =>
            addRecurringCost({
              label: 'Nouveau coût récurrent',
              monthlyAmount: 0,
              categoryId: categories[0]?.id ?? '',
            })
          }
        >
          + Ajouter un coût récurrent
        </Button>
      </section>

      <Separator />

      {/* Résumé FR1.5 */}
      <section className="flex flex-col gap-1 rounded-md bg-muted/50 p-3 text-sm">
        {rampEnabled ? (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Coût mensuel (initial → cible)</span>
            <span className="font-semibold tabular-nums">
              {formatEur(teamMonthlyCost(team.headcount))} →{' '}
              {formatEur(teamMonthlyCost(team.headcountRamp?.targetHeadcount ?? team.headcount))}
            </span>
          </div>
        ) : (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Coût mensuel total</span>
            <span className="font-semibold">{formatEur(monthlyCost)}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Coût fixe total</span>
          <span className="font-semibold">{formatEur(totalFixed)}</span>
        </div>
        {(initialDeficit ?? 0) > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Déficit initial</span>
            <span className="font-semibold">{formatEur(initialDeficit ?? 0)}</span>
          </div>
        )}
      </section>
    </div>
  );
}
